import { cs } from '../lib/currency';
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, ArrowRight, Check, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import api from '../services/api';
import { useFeatures } from '../context/FeatureContext';

const EventNotificationBanner = () => {
  const { isPlanFeatureEnabled } = useFeatures();
  const eventsEnabled = isPlanFeatureEnabled ? isPlanFeatureEnabled('events.core') : true;
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dismissing, setDismissing] = useState(null);

  useEffect(() => {
    if (!eventsEnabled) {
      setLoading(false);
      return;
    }
    const fetchEvents = async () => {
      try {
        // Fetch homepage events (public events marked to show)
        const response = await api.get('/events/homepage');
        const allEvents = response.data || [];
        
        // Check which events are dismissed
        try {
          const notifRes = await api.get('/notifications?include_dismissed=true');
          const dismissedEventIds = new Set(
            (notifRes.data.notifications || [])
              .filter(n => n.type === 'event' && n.status === 'dismissed')
              .map(n => n.reference_id)
          );
          // Filter out dismissed events
          const visibleEvents = allEvents.filter(e => !dismissedEventIds.has(e.id));
          setEvents(visibleEvents);
        } catch (e) {
          // If notification check fails, show all events
          setEvents(allEvents);
        }
      } catch (error) {
        console.error('Error fetching homepage events:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, [eventsEnabled]);

  const handleDismiss = async (eventId) => {
    setDismissing(eventId);
    try {
      await api.post(`/notifications/event/${eventId}/dismiss`);
      setEvents(prev => prev.filter(e => e.id !== eventId));
    } catch (error) {
      console.error('Error dismissing event:', error);
    } finally {
      setDismissing(null);
    }
  };

  if (loading || events.length === 0) {
    return null;
  }

  return (
    <div className="space-y-2 mb-6">
      {events.map((event) => (
        <div
          key={event.id}
          className="relative bg-gradient-to-r from-emerald-600 to-blue-600 rounded-lg p-4 text-white shadow-lg"
          data-testid={`event-banner-${event.id}`}
        >
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="hidden sm:flex items-center justify-center w-10 h-10 rounded-full bg-white/20">
                <Calendar className="w-5 h-5" />
              </div>
              <div>
                <p className="font-semibold text-lg">{event.title}</p>
                <p className="text-emerald-200 text-sm">
                  {new Date(event.start_datetime).toLocaleDateString('en-US', {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric'
                  })}
                  {event.location && ` • ${event.location}`}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Link to="/parent/events">
                <Button
                  variant="secondary"
                  size="sm"
                  className="bg-white text-emerald-700 hover:bg-emerald-50"
                >
                  View & RSVP
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => handleDismiss(event.id)}
                disabled={dismissing === event.id}
                className="bg-white/20 text-white hover:bg-white/30 border-0"
              >
                {dismissing === event.id ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Check className="w-4 h-4 mr-1" />
                    Dismiss
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Event stats */}
          <div className="mt-3 pt-3 border-t border-white/20 flex items-center gap-4 text-sm text-emerald-200">
            <span>
              {event.rsvp_counts?.yes?.count || 0} attending
            </span>
            {event.max_capacity && (
              <span>
                {event.rsvp_counts?.total_attending || 0} / {event.max_capacity} spots
              </span>
            )}
            {event.cost > 0 && (
              <span>{cs()}{event.cost.toFixed(2)}</span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default EventNotificationBanner;
